import { createContext, ReactNode, useEffect, useMemo, useState } from 'react';
import {
  DatasetSessionStorageContextType,
  SessionStorageState,
} from '../../types/context';
import { useLocation } from 'react-router';
import useLocalStorageKey from '../../hooks/useLocalStorageKey';
import { getIsFromItemExternalUrl } from '../../utils/getIsFromItemExternalUrl';
import { getDataFromSessionStorage } from './utils/getDataFromSessionStorage';
import { isFromPageReload } from '../../utils/isFromPageReload';

const defaultValues: DatasetSessionStorageContextType = {
  isProvided: false,
  fromItemExternalUrl: undefined,
  getDatasetSessionStorageState: () => {
    throw new Error('getDatasetSessionStorageState must be implemented');
  },
  setDatasetSessionStorageState: () => {
    throw new Error('setDatasetSessionStorageState must be implemented');
  },
};

export const DatasetSessionStorageContext =
  createContext<DatasetSessionStorageContextType>(defaultValues);

const DatasetSessionStorageContextProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const { pathname } = useLocation();
  const localStorageKey = useLocalStorageKey();

  const [isFromExternalUrl, setIsFromExternalUrl] = useState<boolean>(
    getIsFromItemExternalUrl(pathname)
  );

  const setDatasetSessionStorageState = (partialState: SessionStorageState) => {
    const data = getDataFromSessionStorage(localStorageKey);

    const newData = {
      ...data,
      ...partialState,
    };

    window.sessionStorage.setItem(localStorageKey, JSON.stringify(newData));
  };

  const cleanupState = () => {
    const data = getDataFromSessionStorage(localStorageKey);

    if (data.lastLoadedDataset) {
      delete data.lastLoadedDataset;
    }

    // Drop the side-menu search on reload/exit so it only survives in-app redirects.
    if (data.menuSearch) {
      delete data.menuSearch;
    }

    window.sessionStorage.setItem(localStorageKey, JSON.stringify(data));
  };

  const setLoadedDatasetToSession = (datasetId: string) => {
    const data = getDataFromSessionStorage(localStorageKey);

    const newData = {
      ...data,
      lastLoadedDataset: datasetId,
    };

    window.sessionStorage.setItem(localStorageKey, JSON.stringify(newData));
  };

  useEffect(() => {
    const newState = getIsFromItemExternalUrl(pathname);

    if (newState) {
      setDatasetSessionStorageState({ reloadFromItem: undefined });
    }

    setIsFromExternalUrl(newState);
  }, [pathname, localStorageKey]);

  // A restored tab (reopen closed tab, session restore) keeps sessionStorage
  // from its previous life. Drop the reload marker unless this load really is
  // a reload, so a stale flag cannot bounce a later detail to the list.
  useEffect(() => {
    if (isFromPageReload()) return;

    const data = getDataFromSessionStorage(localStorageKey);
    if (data.reloadFromItem === undefined) return;

    setDatasetSessionStorageState({ reloadFromItem: undefined });
  }, []);

  // Track pathname across full reloads; consumers compare previous vs current.
  useEffect(() => {
    const data = getDataFromSessionStorage(localStorageKey);

    if (data.lastPathname === pathname) return;

    setDatasetSessionStorageState({
      previousPathname: data.lastPathname,
      lastPathname: pathname,
    });
  }, [pathname, localStorageKey]);

  useEffect(() => {
    window.addEventListener('beforeunload', cleanupState);

    return () => {
      window.removeEventListener('beforeunload', cleanupState);
    };
  }, []);

  const value = useMemo<DatasetSessionStorageContextType>(
    () => ({
      isProvided: true,
      fromItemExternalUrl: isFromExternalUrl,
      getDatasetSessionStorageState: () =>
        getDataFromSessionStorage(localStorageKey),
      setDatasetSessionStorageState,
    }),
    [
      isFromExternalUrl,
      localStorageKey,
      setLoadedDatasetToSession,
      setDatasetSessionStorageState,
    ]
  );

  return (
    <DatasetSessionStorageContext.Provider value={value}>
      {children}
    </DatasetSessionStorageContext.Provider>
  );
};

export default DatasetSessionStorageContextProvider;
